import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { LoadingManagerService } from '@iupics-manager/managers/loading-state-manager/loading-state-manager.servicer';
import {
  IupicsCookieService,
  LocalStorageIupics,
} from '@iupics-manager/managers/security-manager/cookies/iupics-cookie.service';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { Global } from '@iupics-manager/models/global-var';
import { LanguageSelectionService } from '@web-desktop/components/menu-top/controllers/language-selection/language-selection.service';
import { environment } from 'environments/environment';
import jwt_decode from 'jwt-decode';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import { concatMap, firstValueFrom, forkJoin, from, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PortalAccessGuard extends KeycloakAuthGuard {
  private readonly loadingManagerService = inject(LoadingManagerService);
  private connectorService = inject(SecurityManagerService);
  private cookieService = inject(IupicsCookieService);
  private languageSelectionService = inject(LanguageSelectionService);

  isContextInitialized = false;

  constructor(
    protected readonly router: Router,
    protected readonly keycloak: KeycloakService
  ) {
    super(router, keycloak);
  }

  public async isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // Force the user to log in if currently unauthenticated.
    if (!this.authenticated) {
      await this.keycloak.login({
        redirectUri: window.location.origin + state.url,
      });
    }

    if (!this.isContextInitialized) {
      const request$ = forkJoin([
        from(this.keycloak.getToken()).pipe(
          tap((token) => {
            const decodedToken = jwt_decode(token);
            let language = environment.default_language;
            if (decodedToken['session'] && decodedToken['session']['webSession']) {
              language = decodedToken['session']['webSession'];
            }

            // Generate Crypto Key
            this.generateCryptoKey();
            // Generate Device ID
            this.generateDeviceId();
            // Init Application language and select prefered language
            this.initLanguages(language);
          })
        ),
        from(this.keycloak.loadUserProfile()).pipe(
          concatMap((profile) => this.connectorService.initContext(profile)),
          tap(() => (this.isContextInitialized = true))
        ),
      ]);

      await this.loadingManagerService.load(() => firstValueFrom(request$));
    }

    return this.checkAccess(route);
  }

  checkAccess(route: ActivatedRouteSnapshot) {
    // Get the roles required from the route.
    const requiredRoles = route.data.roles;

    // Allow the user to to proceed if no additional roles are required to access the route.
    if (!(requiredRoles instanceof Array) || requiredRoles.length === 0) {
      return true;
    }

    // Allow the user to proceed if all the required roles are present.
    return requiredRoles.every((role) => this.roles.includes(role));
  }

  generateCryptoKey(): void {
    if (!this.cookieService.checkLS(LocalStorageIupics.crypto_key)) {
      this.cookieService.generateRandomKeyCookie();
    }
  }

  generateDeviceId(): void {
    if (!this.cookieService.checkLS(LocalStorageIupics.device_id)) {
      this.cookieService.setAndEncryptLocalStorage(LocalStorageIupics.device_id, Global.device);
    }
  }

  initLanguages(language: string): void {
    this.languageSelectionService.initLanguages(language);
  }
}
